import { t } from '@lingui/core/macro'
import { useLingui } from '@lingui/react'
import { Button } from 'designSystem/components/Button'
import { Separator } from 'designSystem/components/Separator'
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from 'designSystem/components/Tooltip'
import { useMediaQuery } from 'designSystem/components/useMediaQuery'
import {
  KNOWLEDGE_DOC_SIDEBAR_TOGGLE,
  useGeckoAnalytics,
} from 'features/analytics'
import { transformSystemDocumentToTableRow } from 'features/knowledgeDocumentViewer/components/advancedSearch/utils/helpers'
import { useKnowledgeDocumentUXContext } from 'features/knowledgeDocumentViewer/contexts/KnowledgeDocumentUXContext'
import { useSearchActions } from 'features/knowledgeDocumentViewer/contexts/SearchActionsContext'
import { useDocumentFullscreen } from 'features/knowledgeDocumentViewer/hooks/useDocumentFullscreen'
import { useSystemDocumentByBqId } from 'features/knowledgeDocumentViewer/hooks/useSystemDocumentsByBqIds'
import { type TabHistoryItem } from 'features/knowledgeDocumentViewer/types'
import {
  getSystemDocumentDisplayTitle,
  getSystemDocumentDrawingNumber,
  type SystemDocumentEntry,
} from 'features/knowledgeDocumentViewer/types/systemDocumentEntries'
import { PermissionsGate } from 'features/permissions'
import {
  ArrowLeft,
  ArrowRight,
  FileText,
  LockKeyhole,
  Maximize2,
  MoreHorizontal,
  PanelLeft,
  Search,
  Star,
  Tags,
} from 'lucide-react'
import { useCallback, useMemo, useState } from 'react'
import styled from 'styled-components'
import { PERMISSION_CANTILEVER_LABELING_EDIT } from 'types'
import { PHONE_LANDSCAPE_QUERY } from 'utils/mobileLayout'

import { DocumentHeaderTitle } from './DocumentHeaderTitle'
import { DownloadDocumentButton } from './DownloadDocumentButton'
import { EvalSessionHeaderBar } from './EvalSessionHeaderBar'
import { PageSelector } from './PageSelector'
import { PrintDocumentButton } from './PrintDocumentButton'
import { SearchResultsNavigator } from './SearchResultsNavigator'
import { SettingsMenu } from './SettingsMenu'

const StyledHeader = styled.header`
  display: flex;
  align-items: center;
  padding-left: 0.75rem;
  gap: 0.75rem;
  flex-shrink: 0;
  border-bottom: 1px solid var(--border);
`

const MobileHeader = styled.header`
  display: flex;
  flex-direction: column;
  flex-shrink: 0;
  border-bottom: 1px solid var(--border);
`

const MobilePrimaryRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding-inline: 0.75rem;
`

const MobileActionsRow = styled.div<{ $variant?: 'actions' | 'pager' }>`
  display: flex;
  flex-wrap: ${({ $variant }) => ($variant === 'pager' ? 'nowrap' : 'wrap')};
  align-items: center;
  gap: ${({ $variant }) => ($variant === 'pager' ? '0.75rem' : '0.25rem')};
  padding: 0.375rem 0.75rem;
  border-top: 1px solid var(--border);
`

const MobilePageNavigator = styled.div`
  display: flex;
  flex: 1;
  justify-content: flex-end;
  min-width: 0;
`

const ButtonsContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 0.25rem;
`

const TrailingButtonsContainer = styled(ButtonsContainer)`
  padding-block: 0.375rem;
  padding-right: 0.75rem;
`

const BackFwdContainer = styled(ButtonsContainer)`
  gap: 1px;

  & > button:first-child {
    border-radius: 0.25rem 0 0 0.25rem;
  }

  & > button:last-child {
    border-radius: 0 0.25rem 0.25rem 0;
  }
`

const PageSelectorContainer = styled.div`
  flex-shrink: 0;
`

const FavoriteStarIcon = styled(Star)<{ $isFavorited: boolean }>`
  color: ${(props) =>
    props.$isFavorited
      ? props.theme.forecast.defaultText
      : props.theme.forecast.secondaryText};

  button:hover & {
    color: ${(props) => props.theme.forecast.defaultText};
  }
`

/** Name of a nav target for its arrow tooltip, or '' when it can't be named. */
function navTargetLabel(
  item: TabHistoryItem | undefined,
  documentEntry: SystemDocumentEntry | undefined
): string {
  if (!item) return ''
  if (item.type === 'document') {
    if (!documentEntry) return ''
    // Prefer the drawing number; fall back to the display title.
    return (
      getSystemDocumentDrawingNumber(documentEntry) ??
      getSystemDocumentDisplayTitle(documentEntry) ??
      ''
    )
  }
  if (item.type === 'destination') return item.label
  // A DocChat/chat session is a search entry carrying a docChat conversation.
  if (item.type === 'search' && item.searchState.docChat) {
    return t`Chat Session`
  }
  return ''
}

interface KnowledgeDocumentHeaderBarProps {
  compact: boolean
  isThumbnailSidebarOpen: boolean
  onToggleThumbnailSidebar: () => void
  isMultiPage: boolean
  onOpenFindInDocument?: () => void
  isLotoPanelOpen: boolean
  onToggleLotoPanel?: () => void
}

export function KnowledgeDocumentHeaderBar({
  compact,
  isThumbnailSidebarOpen,
  onToggleThumbnailSidebar,
  isMultiPage,
  onOpenFindInDocument,
  isLotoPanelOpen,
  onToggleLotoPanel,
}: KnowledgeDocumentHeaderBarProps) {
  useLingui()
  const { trackEvent } = useGeckoAnalytics()
  const {
    currentDocumentId,
    isSidebarCollapsed,
    setIsSidebarCollapsed,
    isLabelerMode,
    setIsLabelerMode,
    openTabs,
    activeTabId,
    canGoBack,
    canGoForward,
    goBack,
    goForward,
    isOnLinkedDocuments,
    currentLinkedDocumentsData,
    activeHistoryItem,
    isEvalSessionLocked,
    onExitEvalSession,
  } = useKnowledgeDocumentUXContext()
  const { enterDocumentFullscreen } = useDocumentFullscreen()
  const isLandscapePhone = useMediaQuery(PHONE_LANDSCAPE_QUERY)
  const [isMobileActionsOpen, setIsMobileActionsOpen] = useState(false)

  // Present only after a doc-reference (OPC / drawing-number) jump: names the
  // document the back arrow returns to, used until its metadata resolves.
  const referenceOrigin =
    activeHistoryItem?.type === 'document'
      ? activeHistoryItem.referenceOrigin
      : undefined

  // Neighboring history entries feed the back/forward arrow tooltips so the user
  // sees which document each arrow navigates to.
  const activeTab = openTabs.find((tab) => tab.id === activeTabId)
  const history = activeTab?.history ?? []
  const currentIndex = activeTab?.currentIndex ?? 0
  const backItem = canGoBack ? history[currentIndex - 1] : undefined
  const forwardItem = canGoForward ? history[currentIndex + 1] : undefined

  const { isFavorited, toggleFavorite, favoritesDisabled, favoritesSaving } =
    useSearchActions()
  const { document: currentSystemDocument } = useSystemDocumentByBqId(
    currentDocumentId || undefined
  )
  const { document: backTargetDocument } = useSystemDocumentByBqId(
    backItem?.type === 'document' ? backItem.documentId : undefined
  )
  const { document: forwardTargetDocument } = useSystemDocumentByBqId(
    forwardItem?.type === 'document' ? forwardItem.documentId : undefined
  )

  // A doc-reference jump already carries the origin's label (drawing number);
  // otherwise name the previous entry (document, destination, or chat session).
  const backLabel =
    referenceOrigin?.label || navTargetLabel(backItem, backTargetDocument)
  const forwardLabel = navTargetLabel(forwardItem, forwardTargetDocument)
  const backTooltip = backLabel || t`Back`
  const forwardTooltip = forwardLabel || t`Forward`

  const referenceText = currentLinkedDocumentsData?.referenceText ?? ''
  const referenceOriginLabel = referenceOrigin?.label ?? ''

  const currentDocTableRow = useMemo(() => {
    if (!currentSystemDocument) return null
    return transformSystemDocumentToTableRow(currentSystemDocument)
  }, [currentSystemDocument])

  const isCurrentFavorited = currentDocumentId
    ? isFavorited(currentDocumentId)
    : false

  const handleBack = useCallback(() => {
    if (onExitEvalSession) {
      onExitEvalSession()
      return
    }
    goBack(activeTabId)
  }, [activeTabId, goBack, onExitEvalSession])

  const handleForward = useCallback(() => {
    goForward(activeTabId)
  }, [activeTabId, goForward])

  const handleToggleFavorite = useCallback(() => {
    if (currentDocTableRow) {
      toggleFavorite(currentDocTableRow)
    }
  }, [currentDocTableRow, toggleFavorite])

  function handleToggleLabelerMode() {
    setIsLabelerMode((on) => !on)
  }

  function handleOpenFindInDocument() {
    setIsMobileActionsOpen(false)
    onOpenFindInDocument?.()
  }

  function handleSidebarToggle() {
    trackEvent(KNOWLEDGE_DOC_SIDEBAR_TOGGLE.title, {
      action: isSidebarCollapsed ? 'expand' : 'collapse',
    })
    setIsSidebarCollapsed(!isSidebarCollapsed)
  }

  if (isEvalSessionLocked) {
    return <EvalSessionHeaderBar />
  }

  const backForwardButtons = (
    <BackFwdContainer>
      <Tooltip>
        <TooltipTrigger asChild>
          <Button
            variant='inputRaised'
            size='icon-lg'
            aria-label={backTooltip}
            disabled={!canGoBack}
            onClick={handleBack}>
            <ArrowLeft size={18} />
          </Button>
        </TooltipTrigger>
        <TooltipContent side='bottom' sideOffset={8} zIndex='overlay'>
          {backTooltip}
        </TooltipContent>
      </Tooltip>
      <Tooltip>
        <TooltipTrigger asChild>
          <Button
            variant='inputRaised'
            size='icon-lg'
            aria-label={forwardTooltip}
            disabled={!canGoForward}
            onClick={handleForward}>
            <ArrowRight size={18} />
          </Button>
        </TooltipTrigger>
        <TooltipContent side='bottom' sideOffset={8} zIndex='overlay'>
          {forwardTooltip}
        </TooltipContent>
      </Tooltip>
    </BackFwdContainer>
  )

  const referenceOriginButton = referenceOrigin ? (
    <Button
      variant='inputRaised'
      size='default'
      title={t`Back to ${referenceOriginLabel}`}
      onClick={() => goBack(activeTabId)}>
      <ArrowLeft size={14} />
      {t`Back to ${referenceOriginLabel}`}
    </Button>
  ) : null

  const thumbnailToggleButton =
    !isOnLinkedDocuments && isMultiPage ? (
      <Button
        variant='inputRaised'
        size='icon-lg'
        onClick={onToggleThumbnailSidebar}
        title={
          isThumbnailSidebarOpen
            ? t`Hide Page Thumbnails`
            : t`Show Page Thumbnails`
        }>
        <PanelLeft size={18} strokeWidth={1.5} />
      </Button>
    ) : null

  const documentTitle = (
    <DocumentHeaderTitle
      documentId={currentDocumentId}
      showDetails={!compact || !isLandscapePhone}
      customTitle={
        currentLinkedDocumentsData
          ? t`References to ${referenceText}`
          : undefined
      }
    />
  )

  const pageSelector = isOnLinkedDocuments ? null : (
    <PageSelectorContainer>
      <PageSelector />
    </PageSelectorContainer>
  )

  const trailingActions = isOnLinkedDocuments ? null : (
    <>
      <SearchResultsNavigator />
      {onOpenFindInDocument && (
        <Button
          variant='inputRaised'
          size='icon-lg'
          onClick={handleOpenFindInDocument}
          title={t`Find in document (Ctrl+F)`}
          aria-label={t`Find in document`}>
          <Search size={18} strokeWidth={1.5} />
        </Button>
      )}
      {currentDocumentId && currentDocTableRow && (
        <Button
          variant='inputRaised'
          size='icon-lg'
          onClick={handleToggleFavorite}
          disabled={favoritesDisabled}
          aria-busy={favoritesSaving}
          title={
            isCurrentFavorited ? t`Remove from favorites` : t`Add to favorites`
          }>
          <FavoriteStarIcon
            size={18}
            strokeWidth={1.5}
            $isFavorited={isCurrentFavorited}
            fill={isCurrentFavorited ? 'currentColor' : 'none'}
          />
        </Button>
      )}
      {onToggleLotoPanel && (
        <Button
          variant={isLotoPanelOpen ? 'default' : 'inputRaised'}
          size='icon-lg'
          aria-pressed={isLotoPanelOpen}
          onClick={onToggleLotoPanel}
          title={isLotoPanelOpen ? t`Hide LOTO panel` : t`Show LOTO panel`}>
          <LockKeyhole size={18} strokeWidth={1.5} />
        </Button>
      )}
      <PrintDocumentButton />
      <DownloadDocumentButton />

      <SettingsMenu />

      <PermissionsGate requires={[PERMISSION_CANTILEVER_LABELING_EDIT]}>
        <Button
          variant={isLabelerMode ? 'default' : 'inputRaised'}
          size='icon-lg'
          onClick={handleToggleLabelerMode}
          title={isLabelerMode ? t`Exit labeling mode` : t`Labeling mode`}>
          <Tags size={18} strokeWidth={1.5} />
        </Button>
      </PermissionsGate>

      <Button
        variant='inputRaised'
        size='icon-lg'
        onClick={handleSidebarToggle}
        title={isSidebarCollapsed ? t`Show Metadata` : t`Hide Metadata`}>
        <FileText size={18} strokeWidth={1.5} />
      </Button>

      <Button
        variant='inputRaised'
        size='icon-lg'
        onClick={enterDocumentFullscreen}
        title={t`Fullscreen document`}
        aria-label={t`Fullscreen document`}>
        <Maximize2 size={18} strokeWidth={1.5} />
      </Button>
    </>
  )

  if (compact) {
    const showActionsToggle = trailingActions !== null
    return (
      <MobileHeader>
        <MobilePrimaryRow>
          {backForwardButtons}
          {documentTitle}
          {showActionsToggle && (
            <Button
              variant={isMobileActionsOpen ? 'default' : 'inputRaised'}
              size='icon-lg'
              aria-expanded={isMobileActionsOpen}
              aria-label={t`Document actions`}
              title={t`Document actions`}
              onClick={() => setIsMobileActionsOpen((open) => !open)}>
              <MoreHorizontal size={18} strokeWidth={1.5} />
            </Button>
          )}
        </MobilePrimaryRow>
        {showActionsToggle && isMobileActionsOpen && (
          <>
            {isMultiPage && (
              <MobileActionsRow $variant='pager'>
                {thumbnailToggleButton}
                <MobilePageNavigator>{pageSelector}</MobilePageNavigator>
              </MobileActionsRow>
            )}
            <MobileActionsRow>
              {referenceOriginButton}
              {trailingActions}
            </MobileActionsRow>
          </>
        )}
      </MobileHeader>
    )
  }

  return (
    <StyledHeader>
      {backForwardButtons}
      {referenceOriginButton}
      {thumbnailToggleButton}
      {documentTitle}
      {!isOnLinkedDocuments && <Separator orientation='vertical' />}
      <TrailingButtonsContainer>
        {pageSelector}
        {trailingActions}
      </TrailingButtonsContainer>
    </StyledHeader>
  )
}

export default KnowledgeDocumentHeaderBar
